import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { IncidentFormModal } from '../features/iso45001/incidents/IncidentFormModal';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { SafetyIndicesPanel } from '../features/iso45001/incidents/SafetyIndicesPanel';
import {
  createSafetyIncident,
  escalateSafetyIncident,
  fetchSafetyIncidents,
  transitionSafetyIncidentStatus,
  updateSafetyIncident,
} from '../features/iso45001/incidents/incidentsApi';
import type { SafetyIncidentSummary } from '../features/iso45001/incidents/types';
import {
  INCIDENT_TYPE_LABEL,
  SEVERITY_LABEL,
  STATUS_LABEL,
  severityBadgeClass,
  statusBadgeClass,
} from '../features/iso45001/incidents/incidentsUtils';

const NEXT_STATUS: Partial<Record<SafetyIncidentSummary['status'], SafetyIncidentSummary['status']>> = {
  abierto: 'en_investigacion',
  en_investigacion: 'acciones_definidas',
  acciones_definidas: 'cerrado',
};

export function SafetyIncidentsPage() {
  const { canEdit } = usePlatformAccessLevel('iso45001.incidents.access');
  const [items, setItems] = useState<SafetyIncidentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<SafetyIncidentSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchSafetyIncidents({ page, pageSize: 20 });
      setItems(list.data);
      setTotalPages(list.pagination.pages);
    } catch {
      setError('No se pudieron cargar los incidentes SST.');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  const advanceStatus = async (item: SafetyIncidentSummary) => {
    const next = NEXT_STATUS[item.status];
    if (!next) return;
    await transitionSafetyIncidentStatus(item.id, next);
    await load();
  };

  const escalate = async (item: SafetyIncidentSummary) => {
    await escalateSafetyIncident(item.id);
    await load();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Incidentes y accidentes SST</h1>
          <p className="text-muted-foreground text-sm">ISO 45001 — investigación e índices</p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            Registrar incidente
          </button>
        )}
      </div>

      <SafetyIndicesPanel />

      {error && <p className="text-destructive text-sm">{error}</p>}

      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left">Código</th>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Severidad</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Ubicación</th>
              <th className="px-3 py-2 text-left">Días baja</th>
              <th className="px-3 py-2 text-left">Informe</th>
              {canEdit && <th className="px-3 py-2 text-left">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={canEdit ? 9 : 8} className="px-3 py-6 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 9 : 8} className="px-3 py-6 text-center text-muted-foreground">
                  Sin incidentes registrados.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono">{item.code}</td>
                  <td className="px-3 py-2">{new Date(item.occurredAt).toLocaleDateString()}</td>
                  <td className="px-3 py-2">{INCIDENT_TYPE_LABEL[item.type]}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs ${severityBadgeClass(item.severity)}`}
                    >
                      {SEVERITY_LABEL[item.severity]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs ${statusBadgeClass(item.status)}`}
                    >
                      {STATUS_LABEL[item.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2">{item.location}</td>
                  <td className="px-3 py-2">{item.lostDays}</td>
                  <td className="px-3 py-2">
                    <ExportPdfButton
                      reportType="safety_incident"
                      resourceId={item.id}
                      permissionKey="iso45001.incidents.access"
                      className="btn btn-outline btn-xs"
                      label="PDF"
                    />
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 space-x-1">
                      <button
                        type="button"
                        className="btn btn-outline btn-xs"
                        onClick={() => setEditItem(item)}
                      >
                        Investigar
                      </button>
                      {NEXT_STATUS[item.status] && (
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => void advanceStatus(item)}
                        >
                          {STATUS_LABEL[NEXT_STATUS[item.status]!]}
                        </button>
                      )}
                      {!item.bridgeRecordId && item.status !== 'cerrado' && (
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => void escalate(item)}
                        >
                          Escalar CAPA
                        </button>
                      )}
                      {item.bridgeRecordId && (
                        <Link
                          className="text-primary text-xs underline"
                          to={`/registros/no-conformidades/${item.bridgeRecordId}`}
                        >
                          Ver NC
                        </Link>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      <IncidentFormModal
        open={createOpen}
        title="Registrar incidente SST"
        onClose={() => setCreateOpen(false)}
        onSubmit={async (payload) => {
          await createSafetyIncident(payload);
          await load();
        }}
      />

      <IncidentFormModal
        open={Boolean(editItem)}
        title={editItem ? `Investigar ${editItem.code}` : 'Investigar'}
        showInvestigation
        initial={editItem ?? undefined}
        onClose={() => setEditItem(null)}
        onSubmit={async (payload) => {
          if (!editItem) return;
          await updateSafetyIncident(editItem.id, payload);
          await load();
        }}
      />
    </div>
  );
}
